import '../../styles/telegram_auth.css';
import {trackTelegramConnectionSuccess, userBrowserId} from '../../scripts/common.js';
import QRCode from 'qrcode';
import {FormWebsocket} from './_form_websocket.js';

// SVGs
// eslint-disable-next-line max-len
const ICON_TELEGRAM = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.751-.244-1.349-.374-1.297-.789.027-.216.324-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.119.098.152.228.166.331.016.119.032.519.032.54z"/></svg>`;
// eslint-disable-next-line max-len
const ICON_COPY = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;
// eslint-disable-next-line max-len
const ICON_CHECK = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;

export function setupTelegramAuth(survey, options = {}) {
  const config = {
    pageName: 'student_personal_info',
    fields: {email: 'student_email', name: 'student_firstname', surname: 'student_lastname'},
    requiredFields: ['student_email', 'student_firstname', 'student_lastname', 'contact_phone'],
    startUrl: '/api/application_start', statusUrl: '/api/application_status', resetUrl: null,
    codeKey: 'tlf_connection_code', botKey: 'tlf_bot_name', kind: 'application', requireOnComplete: false,
    intro: 'To receive notifications, please connect your Telegram account.',
    success: 'You can now proceed with the application.',
    ...options,
  };
  const containerId = 'telegram-auth-container';
  let socket = null;
  let poll = null;
  let connected = false;
  let generation = 0;
  let disposed = false;
  const tracked = new Set();

  const question = () => survey.getQuestionByName('telegram_auth_widget');
  const container = () => document.getElementById(containerId);
  const active = (version, formId) => !disposed && version === generation && survey.getValue('form_id') === formId;
  const read = key => {try { return localStorage.getItem(key); } catch { return null; }};
  const write = (key, value) => {try {localStorage.setItem(key, value);} catch { /* Connection still works without storage. */ }};
  const remove = key => {try {localStorage.removeItem(key);} catch { /* Storage can be disabled. */ }};
  const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[char]);

  function stopListeners() {
    socket?.close();
    socket = null;
    if (poll) clearInterval(poll);
    poll = null;
  }

  function lockIdentity(locked) {
    config.requiredFields.forEach(name => {
      const field = survey.getQuestionByName(name);
      if (field) field.readOnly = locked;
    });
  }

  function showError(message) {
    const field = question();
    field?.clearErrors();
    field?.addError(message);
  }

  function clearLocalSession() {
    generation++;
    connected = false;
    stopListeners();
    remove(config.codeKey);
    remove(config.botKey);
    survey.setValue('form_id', null);
    lockIdentity(false);
    if (container()) renderStart(container());
  }

  async function reset() {
    const formId = survey.getValue('form_id');
    const version = ++generation;
    connected = false;
    stopListeners();
    if (formId && config.resetUrl) {
      try {
        const response = await fetch(`${BACKEND_URL}${config.resetUrl}`, {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({form_id: formId, idd: userBrowserId}),
        });
        if (!response.ok) throw new Error('Could not reset connection');
      } catch {
        if (active(version, formId)) {
          showError('Could not disconnect Telegram. Please try again.');
          startListeners(formId, version);
        }
        return;
      }
    }
    if (active(version, formId)) clearLocalSession();
  }

  function renderStart(target) {
    target.innerHTML = `<div class="tlf-auth-box tlf-auth-start">
      <p>${escapeHtml(config.intro)}</p>
      <button type="button" class="tlf-btn-primary" id="btn-get-code">Get Verification Code</button>
    </div>`;
    target.querySelector('#btn-get-code').onclick = start;
  }

  async function start() {
    question()?.clearErrors();
    if (config.requiredFields.some(name => survey.getQuestionByName(name)?.hasErrors(true))) return;
    const button = container()?.querySelector('#btn-get-code');
    if (!button) return;
    button.disabled = true;
    button.textContent = 'Generating...';
    lockIdentity(true);
    const version = ++generation;
    const identity = Object.fromEntries(Object.entries(config.fields).map(([key, field]) => [key, survey.getValue(field)]));
    const payload = config.kind === 'application' ? {
      student_email: identity.email, student_firstname: identity.name, student_lastname: identity.surname, idd: userBrowserId,
    } : {...identity, idd: userBrowserId};
    try {
      const response = await fetch(`${BACKEND_URL}${config.startUrl}`, {
        method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (disposed || version !== generation) return;
      if (response.status !== 201 || data.status !== 'ok') {
        throw new Error(data.status === 'already_used_mail'
          ? 'This email is already associated with a submitted application.' : (data.error || 'Could not start connection.'));
      }
      survey.setValue('form_id', data.form_id);
      write(config.codeKey, data.connection_code);
      write(config.botKey, data.bot_name);
      renderConnection(data.connection_code, data.bot_name);
      startListeners(data.form_id, version);
    } catch (error) {
      if (disposed || version !== generation) return;
      showError(error.message || 'Connection error. Please try again.');
      lockIdentity(false);
      renderStart(container());
    }
  }

  function renderConnection(code, botName) {
    const target = container();
    if (!target) return;
    const link = `https://t.me/${encodeURIComponent(botName.replace(/^@/, ''))}?start=${encodeURIComponent(code)}`;
    target.innerHTML = `<div class="tlf-auth-box tlf-auth-connect">
      <h4>Connect Telegram to Proceed</h4>
      <div class="tlf-auth-grid">
        <div class="tlf-qr-col"><canvas id="tlf-qr-canvas"></canvas><div class="tlf-qr-caption">Scan with Phone</div></div>
        <div class="tlf-or-col"><span>OR</span></div>
        <div class="tlf-manual-col">
          <a href="${escapeHtml(link)}" target="_blank" rel="noopener" class="tlf-btn-telegram">${ICON_TELEGRAM} Open Telegram App</a>
          <div>Or send this code to <strong>${escapeHtml(botName)}</strong>:</div>
          <div class="tlf-code-container"><div class="tlf-code-display">${escapeHtml(code)}</div>
            <button type="button" class="tlf-btn-copy" title="Copy Code">${ICON_COPY}</button></div>
        </div>
      </div>
      <div class="tlf-connect-footer">
        <button id="btn-change-email" type="button" class="tlf-btn-link">Change Email / Restart</button>
        <span class="tlf-status-text">Waiting for connection</span>
      </div>
    </div>`;
    QRCode.toCanvas(target.querySelector('canvas'), link, {width: 140, margin: 1}).catch(() => {});
    target.querySelector('#btn-change-email').onclick = reset;
    target.querySelector('.tlf-btn-copy').onclick = async event => {
      const button = event.currentTarget;
      try {
        await navigator.clipboard.writeText(code);
        button.innerHTML = ICON_CHECK;
      } catch {
        showError('Could not copy the code. Please select and copy it manually.');
      }
    };
  }

  function renderConnected(username, formId) {
    const target = container();
    if (!target) return;
    connected = true;
    stopListeners();
    lockIdentity(true);
    question()?.clearErrors();
    if (config.kind === 'application' && !tracked.has(formId)) {
      tracked.add(formId);
      trackTelegramConnectionSuccess();
    }
    target.innerHTML = `<div class="tlf-auth-box tlf-auth-success">
      <div class="tlf-success-content"><h4>✅ Connected${username ? ` as @${escapeHtml(username)}` : ' to Telegram'}</h4>
        <p>${escapeHtml(config.success)}</p></div>
      <button id="btn-unlink" type="button" class="tlf-btn-unlink">Unlink Account</button>
    </div>`;
    target.querySelector('#btn-unlink').onclick = reset;
    // Keep checking club sessions so revocation in another tab locks this form too.
    if (config.kind === 'prepclub') startListeners(formId, generation);
  }

  async function checkStatus(formId, version) {
    try {
      const response = await fetch(`${BACKEND_URL}${config.statusUrl}`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({form_id: formId, idd: userBrowserId}),
      });
      const data = await response.json();
      if (!active(version, formId)) return;
      if (response.status === 404) {
        clearLocalSession();
      } else if (response.ok && data.status === 'ok') {
        if (data.is_connected && !connected) renderConnected(data.tg_username, formId);
        if (!data.is_connected && data.connection_code && data.bot_name) {
          write(config.codeKey, data.connection_code);
          write(config.botKey, data.bot_name);
          renderConnection(data.connection_code, data.bot_name);
        }
      }
    } catch {
      // A transient failure must not discard a valid pending connection.
    }
  }

  function startListeners(formId, version) {
    stopListeners();
    socket = new FormWebsocket(formId, data => {
      if (active(version, formId) && !connected) renderConnected(data.tg_username, formId);
    }, {kind: config.kind, browserId: userBrowserId, onReset: () => {
      if (active(version, formId)) clearLocalSession();
    }});
    poll = setInterval(() => checkStatus(formId, version), 5000);
  }

  function onRender(sender, event) {
    if (event.question.name !== 'telegram_auth_widget') return;
    const target = event.htmlElement.querySelector(`#${containerId}`);
    if (!target) return;
    const formId = survey.getValue('form_id');
    const version = ++generation;
    connected = false;
    stopListeners();
    if (!formId) {
      lockIdentity(false);
      renderStart(target);
      return;
    }
    lockIdentity(true);
    const code = read(config.codeKey), botName = read(config.botKey);
    if (code && botName) renderConnection(code, botName);
    else {
      target.innerHTML = '<div class="tlf-auth-box"><p>Checking Telegram connection...</p><button type="button">Restart connection</button></div>';
      target.querySelector('button').onclick = reset;
    }
    startListeners(formId, version);
    void checkStatus(formId, version);
  }

  function blockIfDisconnected(event) {
    if (connected) return;
    event.allow = false;
    showError('Please connect Telegram before continuing.');
    container()?.scrollIntoView({block: 'center', behavior: 'smooth'});
  }

  const onPageChanging = (sender, event) => {
    if (event.oldCurrentPage?.name === config.pageName && event.newCurrentPage) blockIfDisconnected(event);
  };
  const onCompleting = (sender, event) => {
    if (config.requireOnComplete) blockIfDisconnected(event);
  };
  const onIdentityChanged = (sender, event) => {
    if (config.resetUrl && Object.values(config.fields).includes(event.name) && survey.getValue('form_id')) void reset();
  };
  survey.onAfterRenderQuestion.add(onRender);
  survey.onCurrentPageChanging.add(onPageChanging);
  survey.onCompleting.add(onCompleting);
  survey.onValueChanged.add(onIdentityChanged);

  function dispose(clearStorage = false) {
    disposed = true;
    generation++;
    stopListeners();
    survey.onAfterRenderQuestion.remove(onRender);
    survey.onCurrentPageChanging.remove(onPageChanging);
    survey.onCompleting.remove(onCompleting);
    survey.onValueChanged.remove(onIdentityChanged);
    window.removeEventListener('pagehide', onPageHide);
    if (clearStorage) {
      remove(config.codeKey);
      remove(config.botKey);
    }
  }
  const onPageHide = () => dispose();
  window.addEventListener('pagehide', onPageHide);
  return {dispose, reset, isConnected: () => connected};
}
